/**
 * OCR Invoice Extractor - Main Application Component
 * ==================================================
 * 
 * This is the root component of the React application that orchestrates the entire UI.
 * 
 * Key Features:
 * - Multi-page routing with React Router
 * - Global state management with Context API
 * - Toast notifications for user feedback
 * - Theme management (light/dark mode support)
 * - Authentication and protected routes
 * - Responsive glassmorphism design
 * 
 * Architecture:
 * - Router: Handles client-side navigation between pages
 * - AuthProvider: Manages authentication state
 * - ThemeProvider: Manages light/dark theme state
 * - FilesProvider: Manages uploaded files and processing state
 * - Layout: Consistent header/navigation across all pages
 * - Pages: Individual route components (Login, Home, Dashboard, Results, Settings)
 * - Toaster: Global toast notification system
 * 
 * Routes:
 * /login - Login page (public)
 * / - Home page with file upload (protected)
 * /dashboard - Processing dashboard with controls (protected)
 * /results - View extracted invoice data (protected)
 * /settings - App settings and data management (protected)
 * 
 * State Management:
 * - AuthContext: Authentication state and user session
 * - FilesContext: Shared file upload and processing state
 * - ThemeContext: Theme preference (persisted to localStorage)
 * 
 * @author Your Team
 * @version 1.0.0
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { FilesProvider } from './contexts/FilesContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedLayout from './components/ProtectedLayout';
import ImpersonationBanner from './components/ImpersonationBanner';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import KeyNetRedirectPage from './pages/KeyNetRedirectPage';
/**
 * Main Application Component
 * 
 * Sets up the provider hierarchy and routing structure.
 * Provider order matters - outer providers are available to inner providers.
 * 
 * Provider Hierarchy:
 * 1. Router - Enables routing functionality
 * 2. ThemeProvider - Provides theme state and toggle function
 * 3. AuthProvider - Provides authentication state and functions
 * 4. FilesProvider - Provides file management state and functions
 * 5. Layout - Wraps protected pages with consistent header/nav
 * 6. Routes - Defines page routing with protection
 * 
 * The Toaster component is placed outside providers to ensure it works
 * even if providers fail to initialize.
 */
const App: React.FC = () => {
  return (
    <Router>
      {/* Theme management - wraps everything to provide theme context */}
      <ThemeProvider>
        {/* Authentication management - provides auth state and login/logout */}
        <AuthProvider>
          {/* File state management - provides upload/processing state */}
          <FilesProvider>
            {/* Sticky banner shown app-wide while impersonating a tenant */}
            <ImpersonationBanner />
            {/* Route definitions - maps URLs to page components */}
            <Routes>
              {/* Public routes - Login and Register pages */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              {/* KeyNet SSO landing — stands in for the future auto-
                  redirect. Shown to anyone who arrives at KeyScan without
                  an active KeyNet session. Shown so users who land here
                  directly get a clear path back to their KeyNet portal. */}
              <Route path="/sso" element={<KeyNetRedirectPage />} />

              {/* Protected routes - all other routes require authentication */}
              <Route path="/*" element={<ProtectedLayout />} />
            </Routes>
          </FilesProvider>
        </AuthProvider>
      </ThemeProvider>
      
      {/* 
        Toast Notifications
        Displays temporary success/error messages to the user.
        Configured with glassmorphism styling to match app design.
       */}
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          className: 'glass',
          style: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            color: '#1f2937',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: '#FFFFFF',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: '#FFFFFF',
            },
          },
        }}
      />
    </Router>
  );
};

export default App;